import type {ResponseAnnotation} from '@reados/contracts';
import type {TranscriptSelectionAnchor} from '@reados/renderer';
import {readStored,saveStored} from './state';

export const ANNOTATION_DRAFT_EVENT='reados:annotation-draft';
export interface AnnotationCommentRequest {key:string;token:string;anchor?:TranscriptSelectionAnchor}
export const annotationKey=(item:ResponseAnnotation)=>item.id?`id:${item.id}`:JSON.stringify([item.sourceThreadId,item.sourceMessageId,item.selectedText,item.sourceRanges]);
export function commentRequestFor(annotations:ResponseAnnotation[],anchor?:TranscriptSelectionAnchor):AnnotationCommentRequest|null {
 const last=annotations.at(-1);if(!last)return null;
 const request:AnnotationCommentRequest={key:annotationKey(last),token:crypto.randomUUID()};
 // A live iframe range must never be persisted or sent with the annotation.
 if(anchor)Object.defineProperty(request,'anchor',{value:anchor,enumerable:false});
 return request;
}
export const normalizeAnnotationComment=(value:string|undefined)=>value?.trim()||undefined;
const annotationSnapshot=(item:ResponseAnnotation)=>JSON.stringify([annotationKey(item),item.groupId,item.sourceThreadId,item.sourceMessageId,item.selectedText,item.sourceRanges,normalizeAnnotationComment(item.comment)]);
export function appendAnnotations(current:ResponseAnnotation[],incoming:ResponseAnnotation[]):ResponseAnnotation[]{
 const result=[...current],keys=new Set(current.map(annotationKey));
 for(const item of incoming)if(!keys.has(annotationKey(item))){keys.add(annotationKey(item));result.push(item);}
 if(result.length>20||result.reduce((n,item)=>n+item.selectedText.length,0)>128_000)throw new Error('选中文字过多，请先发送或移除部分选区。');
 return result;
}
/** Persist before notifying the mounted composer, so first-send acceptance can
 * transfer additions made while it was awaiting the fork or turn response. */
export function stageConversationAnnotations(stateKey:string,incoming:ResponseAnnotation[]):ResponseAnnotation[]{
 const current=readStored<ResponseAnnotation[]>(`${stateKey}.annotations`,[]),next=appendAnnotations(current,incoming);
 if(next.length===current.length)return current;
 saveStored(`${stateKey}.annotations`,next);
 saveStored(`${stateKey}.annotationComment`,commentRequestFor(next.slice(current.length)));
 window.dispatchEvent(new CustomEvent(ANNOTATION_DRAFT_EVENT,{detail:{stateKey}}));
 return next;
}
/** Only an explicit save changes the comment; source identity and selection remain intact. */
export function updateAnnotationComment(current:ResponseAnnotation[],key:string,value:string|undefined):ResponseAnnotation[]{
 const comment=normalizeAnnotationComment(value);
 if(comment&&comment.length>16_000)throw new Error('每条评论最多 16,000 字，请缩短后保存。');
 const result:ResponseAnnotation[]=current.map(item=>{
  if(annotationKey(item)!==key)return item;
  const {comment:_,...source}=item;
  return comment?{...source,comment}:source;
 });
 if(result.reduce((total,item)=>total+(item.comment?.length??0),0)>64_000)throw new Error('评论总长度最多 64,000 字，请缩短后保存。');
 return result;
}
export function consumeAnnotations(current:ResponseAnnotation[],submitted:ResponseAnnotation[]):ResponseAnnotation[]{
 // An in-flight submission owns its exact snapshot, not a later edited version.
 const snapshots=new Set(submitted.map(annotationSnapshot));return current.filter(item=>!snapshots.has(annotationSnapshot(item)));
}
/** Track edits by stable identity, including remove/re-add cycles during a send. */
export function changedAnnotationKeys(before:ResponseAnnotation[],after:ResponseAnnotation[]):string[]{
 const previous=new Map(before.map(item=>[annotationKey(item),annotationSnapshot(item)]));
 const next=new Map(after.map(item=>[annotationKey(item),annotationSnapshot(item)]));
 return [...new Set([...previous.keys(),...next.keys()])].filter(key=>previous.get(key)!==next.get(key));
}
/** A failed send restores only identities the user has not changed since submit. */
export function restoreAnnotations(current:ResponseAnnotation[],submitted:ResponseAnnotation[],editedKeys:ReadonlySet<string>=new Set()):ResponseAnnotation[]{
 const currentKeys=new Set(current.map(annotationKey));
 return [...submitted.filter(item=>!currentKeys.has(annotationKey(item))&&!editedKeys.has(annotationKey(item))),...current];
}
